import { formatNOK } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import type { OfferLine } from "@/hooks/use-offers";
import { cn } from "@/lib/utils";

interface Props {
  line: OfferLine;
  isFirst: boolean;
  isLast: boolean;
  isLocked: boolean;
  onTap: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate?: () => void;
}

/**
 * Mobile-display av en tilbudslinje. Tap åpner OfferLineEditSheet via
 * parent. Møve-knapper bytter sort_order med naboline (i samme seksjon).
 * Erstatter OfferLineRow på smale viewports.
 */
export function OfferLineCard({
  line,
  isFirst,
  isLast,
  isLocked,
  onTap,
  onMoveUp,
  onMoveDown,
  onDuplicate,
}: Props) {
  return (
    <div
      className={cn(
        "border rounded-md bg-background overflow-hidden",
        isLocked && "opacity-70",
      )}
    >
      <button
        type="button"
        onClick={onTap}
        disabled={isLocked}
        className="w-full text-left p-3 hover:bg-muted/30 disabled:cursor-not-allowed"
      >
        <p className="font-medium text-sm truncate">
          {line.description}
          {isLocked && (
            <SrIcon
              name="lock"
              className="inline-block w-3 h-3 ml-1.5 text-muted-foreground"
            />
          )}
          {line.source_idea_card_id && (
            <span className="ml-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Fra idé
            </span>
          )}
        </p>
        <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
          <span>
            {line.quantity} {line.unit} × {formatNOK(line.unit_price)}
          </span>
          <span className="tabular-nums font-medium text-foreground">
            {formatNOK(line.amount)}
          </span>
        </div>
      </button>
      {!isLocked && (
        <div className="flex border-t bg-muted/10">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            className="flex-1 py-2 text-muted-foreground hover:bg-muted/30 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
            aria-label="Flytt opp"
          >
            <SrIcon name="chevron-up" className="w-4 h-4 mx-auto" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            className="flex-1 py-2 text-muted-foreground hover:bg-muted/30 disabled:opacity-30 disabled:hover:bg-transparent border-l transition-colors"
            aria-label="Flytt ned"
          >
            <SrIcon name="chevron-down" className="w-4 h-4 mx-auto" />
          </button>
          {onDuplicate && (
            <button
              type="button"
              onClick={onDuplicate}
              className="flex-1 py-2 text-muted-foreground hover:bg-muted/30 border-l transition-colors"
              aria-label="Dupliser linje"
            >
              <SrIcon name="copy" className="w-4 h-4 mx-auto" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
